import { useCallback, useEffect, useMemo, useState } from 'react';

import { toastError, toastSuccess } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';
import { useMixPanelTrakingEventMutation } from '@/api/commonApis';

import { useAddScenarioMutation } from '../api/costingAgentApi';
import {
	getEmptyScenarioChanges,
	mergeDefinedValues,
	COSTING_MIX_PANEL_EVENTS,
	SCENARIO_RE_COST_FIELDS,
	SCENARIO_SECTION_CHANGE_TYPES
} from '../utils';

import CostingHeader from './CostingHeader';
import CostingTableAndSummary from './CostingTableAndSummary';

const OptimizeAndOffer = ({
	costingBrandInfo,
	productId,
	historyDetailId,
	historyId,
	payload,
	isEditable,
	validationErrors,
	onScenarioReCost,
	onHistoryRefetch,
	onCostingRefetch,
	onOrderQuantityChange,
	onBaseScenarioSelectedChange
}) => {
	const [ocsOfferInfo, setOcsOfferInfo] = useState(null);
	const [selectedScenarioId, setSelectedScenarioId] = useState(null);
	const [orderQuantity, setOrderQuantity] = useState(null);
	const [scenarioChanges, setScenarioChanges] = useState(getEmptyScenarioChanges);
	const [scenarioRefreshKey, setScenarioRefreshKey] = useState(0);
	const [addScenario, { isLoading: isAddScenarioLoading }] = useAddScenarioMutation();
	const [mixPanelTrakingEvent] = useMixPanelTrakingEventMutation();
	const sortedOcsOfferInfo = useMemo(() => {
		return [...(ocsOfferInfo || [])].sort((firstScenario, secondScenario) => {
			const firstScenarioNumber = Number(firstScenario?.name?.match(/\d+$/)?.[0]);
			const secondScenarioNumber = Number(secondScenario?.name?.match(/\d+$/)?.[0]);

			return firstScenarioNumber - secondScenarioNumber;
		});
	}, [ocsOfferInfo]);

	const selectedScenario =
		sortedOcsOfferInfo.find((scenario) => scenario.name === selectedScenarioId) || sortedOcsOfferInfo[0];
	const isBaseScenarioSelected = selectedScenario?.name === 'SCENARIO_1';
	const selectedScenarioCostingDetails = selectedScenario?.costingDetails || payload || {};
	const qtyWiseHistoryId = isBaseScenarioSelected ? historyId : undefined;
	// the header reports offerInfo[0]'s quantity after a fetch, not the selected scenario's —
	// use the scenario's own saved quantity so it survives a refresh and a tab switch
	const summaryOrderQuantity = selectedScenario?.quantity ?? orderQuantity;

	useEffect(() => {
		if (!selectedScenarioId && sortedOcsOfferInfo.length > 0) {
			setSelectedScenarioId(sortedOcsOfferInfo[0]?.name);
		}
	}, [sortedOcsOfferInfo, selectedScenarioId]);

	useEffect(() => {
		setScenarioChanges(getEmptyScenarioChanges());
	}, [selectedScenarioId]);

	// the page header shows History only while the base scenario is selected
	useEffect(() => {
		onBaseScenarioSelectedChange?.(isBaseScenarioSelected);
	}, [isBaseScenarioSelected, onBaseScenarioSelectedChange]);

	const getScenarioDisplayName = (name) => {
		const scenarioNumber = Number(name?.match(/\d+$/)?.[0]);

		if (scenarioNumber === 1) {
			return 'Base Scenario';
		}

		const letterIndex = Number.isFinite(scenarioNumber) ? scenarioNumber - 2 : 0;

		return `Scenario ${String.fromCharCode(65 + letterIndex)}`;
	};

	const handleCreateScenario = async () => {
		if ((ocsOfferInfo?.length || 0) >= 3) {
			toastError('You can create a maximum of 3 scenarios.');
			return;
		}
		const previousScenarioCostingId = selectedScenarioCostingDetails?.id;

		try {
			await addScenario({
				productId,
				initialCostingId: costingBrandInfo?.initialCostingId,
				requestBody: {
					techpackCostingId: previousScenarioCostingId
				}
			}).unwrap();
			toastSuccess('New scenario created successfully');
			setScenarioRefreshKey((currentKey) => currentKey + 1);
		} catch (error) {
			toastError(error?.message || 'Failed to create a new scenario.');
			return;
		}
		const scenarioNumber = ocsOfferInfo?.length || 0;
		const newScenario = {
			...selectedScenario,
			name: `SCENARIO_${scenarioNumber + 1}`
		};
		setOcsOfferInfo((currentScenarios) => [...currentScenarios, newScenario]);
		setSelectedScenarioId(newScenario.name);
	};

	const updateSelectedScenarioCostingDetails = (section, data) => {
		if (isBaseScenarioSelected) {
			return;
		}

		setScenarioChanges((prev) => ({
			...prev,
			hasChanges: true,
			sections:
				section && SCENARIO_SECTION_CHANGE_TYPES[section]
					? new Set([...prev.sections, SCENARIO_SECTION_CHANGE_TYPES[section]])
					: prev.sections
		}));

		setOcsOfferInfo((currentScenarios) =>
			currentScenarios?.map((scenario) => {
				if (scenario.name !== selectedScenario?.name) {
					return scenario;
				}

				return {
					...scenario,
					costingDetails: {
						...scenario.costingDetails,
						[section]: data
					}
				};
			})
		);
	};

	const trackScenarioChangedField = (fieldName) => {
		if (!fieldName) return;
		setScenarioChanges((prev) => ({
			...prev,
			fields: new Set([...prev.fields, fieldName])
		}));
	};

	const updateSelectedScenarioWithResponse = (scenarioResponse) => {
		if (Array.isArray(scenarioResponse)) {
			setOcsOfferInfo(scenarioResponse);
		} else {
			setOcsOfferInfo((currentScenarios) =>
				currentScenarios?.map((scenario) => {
					if (scenario.name !== selectedScenario?.name) {
						return scenario;
					}

					return {
						...scenario,
						...scenarioResponse,
						costingDetails: mergeDefinedValues(
							scenario.costingDetails,
							scenarioResponse?.costingDetails || scenarioResponse
						),
						takeFetchedCostingDetails: true
					};
				})
			);
		}

		setScenarioChanges(getEmptyScenarioChanges());
	};

	// keep already-loaded costing details so a background scenario list refresh
	// doesn't overwrite the just re-costed table with stale/missing data
	const handleScenarioDataListUpdate = useCallback((fetchedScenarios) => {
		if (!Array.isArray(fetchedScenarios)) {
			setOcsOfferInfo(fetchedScenarios);
			return;
		}

		setOcsOfferInfo((currentScenarios) =>
			fetchedScenarios.map((fetchedScenario) => {
				const existingScenario = currentScenarios?.find(
					(scenario) => scenario.name === fetchedScenario.name
				);

				if (existingScenario?.takeFetchedCostingDetails && fetchedScenario.costingDetails) {
					return fetchedScenario;
				}

				return {
					...fetchedScenario,
					costingDetails: existingScenario?.costingDetails || fetchedScenario.costingDetails
				};
			})
		);
	}, []);

	// only the main payload is refetched after a qty-wise save, so patch the saved list into the selected scenario
	const handleQtyWisePricesSaved = (qtyWisePriceList) => {
		setOcsOfferInfo((currentScenarios) =>
			currentScenarios?.map((scenario) => {
				// without its own costingDetails the scenario falls back to payload — don't replace that with a stub
				if (scenario.name !== selectedScenario?.name || !scenario.costingDetails) {
					return scenario;
				}

				return {
					...scenario,
					costingDetails: { ...scenario.costingDetails, qtyWisePriceList }
				};
			})
		);
	};

	// keep the header's quantity locally — the parent is told the selected scenario's one below
	const handleOrderQuantityChange = useCallback((quantity) => {
		setOrderQuantity(quantity);
	}, []);

	// the header always reports the first scenario's quantity; the parent needs the selected scenario's
	useEffect(() => {
		if (summaryOrderQuantity != null) {
			onOrderQuantityChange?.(summaryOrderQuantity);
		}
	}, [summaryOrderQuantity, onOrderQuantityChange]);

	const handleScenarioReCost = async () => {
		const scenarioResponse = await onScenarioReCost?.(
			selectedScenarioCostingDetails,
			Array.from(scenarioChanges.sections),
			Array.from(scenarioChanges.fields),
			selectedScenario?.costingDetails?.id
		);
		if (scenarioResponse) {
			updateSelectedScenarioWithResponse(scenarioResponse);
			setScenarioRefreshKey((currentKey) => currentKey + 1);
		}
	};

	const handleScenarioReCostIndividually = async (changeType, data) => {
		if (isBaseScenarioSelected) {
			return;
		}

		const dataField = SCENARIO_RE_COST_FIELDS[changeType];
		const nextScenarioCostingDetails = dataField
			? { ...selectedScenarioCostingDetails, [dataField]: data }
			: selectedScenarioCostingDetails;

		const scenarioResponse = await onScenarioReCost?.(
			nextScenarioCostingDetails,
			[changeType],
			Array.from(scenarioChanges.fields),
			selectedScenario?.costingDetails?.id
		);
		if (scenarioResponse) {
			updateSelectedScenarioWithResponse(scenarioResponse);
			setScenarioRefreshKey((currentKey) => currentKey + 1);
		}
	};

	return (
		<LoaderComponent loading={isAddScenarioLoading}>
			<div className="marketing-context-container optimize-container mt-4">
				<div className="value-engineering-studio">
					<div className="scenario-tabs scenario-list d-flex mt-3">
						{sortedOcsOfferInfo.map((scenario) => (
							<button
								className={`button scenario-tab scenario-card position-relative ${
									scenario.name === selectedScenarioId ? 'active' : ''
								}`}
								key={scenario?.name}
								type="button"
								onClick={() => setSelectedScenarioId(scenario.name)}
							>
								<span className="scenario-card-title regular-12 weight-600">
									{getScenarioDisplayName(scenario.name)}{' '}
								</span>
								<span className="scenario-card-value regular-14 weight-600">
									{scenario?.offeredFobPrice != null
										? `$${scenario?.offeredFobPrice?.toFixed(2)}`
										: ''}
								</span>
							</button>
						))}

						<button
							className={`button scenario-card new-scenario white-space-nowrap d-block regular-14 gray_dark_02 ${(ocsOfferInfo?.length || 0) >= 3 ? 'cursor-none' : ''}`}
							type="button"
							onClick={() => {
								mixPanelTrakingEvent({
									type: COSTING_MIX_PANEL_EVENTS.NEW_SCENARIO_CLICK,
									body: {
										name: `SCENARIO_${(ocsOfferInfo?.length || 0) + 1}`
									}
								});
								handleCreateScenario();
							}}
						>
							+ New scenario
						</button>
					</div>
				</div>
				<div className="scenario-wise-content mt-4">
					<CostingHeader
						productId={productId}
						initialCostingId={costingBrandInfo?.initialCostingId}
						historyId={qtyWiseHistoryId}
						totalFobPerUnit={payload?.predictedCost || 0}
						baseQty={payload?.quantity}
						quantityWisePrices={selectedScenarioCostingDetails?.qtyWisePriceList || []}
						onHistoryRefetch={onHistoryRefetch}
						onCostingRefetch={onCostingRefetch}
						historyDetailId={historyDetailId}
						requestBodyTechpackCostingId={selectedScenarioCostingDetails?.id}
						scenarioDataList={handleScenarioDataListUpdate}
						selectedScenarioId={selectedScenarioId}
						refreshKey={scenarioRefreshKey}
						costingBrandInfo={costingBrandInfo}
						isFromOptimizeAndOffer={true}
						onOrderQuantityChange={handleOrderQuantityChange}
						costingId={selectedScenarioCostingDetails?.id}
						onQtyWisePricesSaved={handleQtyWisePricesSaved}
					/>

					<div className="costing-table-container relative">
						<CostingTableAndSummary
							payload={selectedScenarioCostingDetails}
							responsePayload={selectedScenarioCostingDetails}
							updateFormData={updateSelectedScenarioCostingDetails}
							isEditable={isEditable && !isBaseScenarioSelected}
							validationErrors={validationErrors}
							reCostIndividually={handleScenarioReCostIndividually}
							trackChangedField={trackScenarioChangedField}
							hasChanges={scenarioChanges.hasChanges}
							onReCost={handleScenarioReCost}
							historyId={qtyWiseHistoryId}
							costingBrandInfo={costingBrandInfo}
							showActionButtons={!isBaseScenarioSelected && scenarioChanges.hasChanges}
							showSummaryActionButtons={false}
							headerOrderQuantity={summaryOrderQuantity}
							quantityWisePrices={selectedScenarioCostingDetails?.qtyWisePriceList || []}
							baseQty={payload?.quantity}
							onQtyWisePricesSaved={handleQtyWisePricesSaved}
						/>
					</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default OptimizeAndOffer;
